import { Camera, Loader2 } from "lucide-react";
import React, { useRef, useState } from "react";
import { useUser } from "@clerk/clerk-react";
import { toast } from "sonner";

import { cn } from "@/lib/utils";
import SettingCard from "./SettingCard";
import avatar4 from "../../../../assets/img/avatars/avatar4.png";
import { toastError } from "../../../../utils/showApiErrorToast";

interface AvatarSettingProps {
  avatarImage: string;
}

const AvatarSetting: React.FC<AvatarSettingProps> = ({ avatarImage }) => {
  const { user } = useUser();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isPngImage, setIsPngImage] = useState(false);

  React.useEffect(() => {
    if (avatarImage) {
      const lowerUrl = avatarImage.toLowerCase();
      const isPng =
        lowerUrl.includes(".png") ||
        lowerUrl.includes("format=png") ||
        lowerUrl.includes("type=png") ||
        lowerUrl.includes("image/png");
      setIsPngImage(isPng);
    } else {
      setIsPngImage(false);
    }
  }, [avatarImage]);

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      toastError("Invalid file type", { description: "Please select an image file." });
      return;
    }

    const fileIsPng = file.type === "image/png" || file.name.toLowerCase().endsWith(".png");
    setIsPngImage(fileIsPng);

    if (file.size > 5 * 1024 * 1024) {
      toastError("File too large", { description: "Please select an image smaller than 5MB." });
      return;
    }

    setIsUploading(true);

    try {
      await user?.setProfileImage({ file });
      toast.success("Profile picture updated", {
        description: "Your profile picture has been updated successfully.",
      });
      window.location.reload();
    } catch (error) {
      console.error("Error updating profile picture:", error);
      toastError("Update failed", {
        description: "Failed to update profile picture. Please try again.",
      });
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const handleAvatarClick = () => {
    if (!isUploading && fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const avatarComponent = (
    <button
      type="button"
      className={cn(
        "group/avatar-setting relative size-20 shrink-0 rounded-full border-0 bg-transparent p-0",
        isUploading ? "cursor-not-allowed" : "cursor-pointer"
      )}
      onClick={handleAvatarClick}
      disabled={isUploading}
      aria-label="Upload profile picture"
    >
      <span
        className={cn(
          "relative flex size-full items-center justify-center overflow-hidden rounded-full border-2 border-border shadow-[0_2px_8px_rgba(0,0,0,0.1)] dark:shadow-[0_2px_8px_rgba(0,0,0,0.35)]",
          isPngImage ? "bg-card p-0.5" : "bg-transparent p-0"
        )}
      >
        <img
          src={avatarImage || avatar4}
          alt="Profile"
          className={cn(
            "size-full rounded-full object-cover",
            isPngImage && "bg-card",
            isUploading && "opacity-70"
          )}
        />
      </span>
      {isUploading ? (
        <span className="absolute inset-0 z-[1] flex items-center justify-center rounded-full bg-black/50">
          <Loader2 className="size-4 animate-spin text-white" aria-hidden />
        </span>
      ) : (
        <span
          className={cn(
            "avatar-overlay absolute z-[1] flex items-center justify-center rounded-full bg-black/10 opacity-0 transition-opacity group-hover/avatar-setting:opacity-100 dark:bg-white/10",
            isPngImage ? "inset-0.5" : "inset-0"
          )}
        >
          <Camera className="size-5 text-white" aria-hidden />
        </span>
      )}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileChange}
        className="hidden"
      />
    </button>
  );

  return (
    <SettingCard
      title="Avatar"
      description="This is your avatar. Click on the avatar to upload a custom one from your files."
      rightContent={avatarComponent}
    />
  );
};

export default AvatarSetting;
